import { useContext, useEffect } from "react";

import { ActionContext } from "@/models/ActionsProvider";
import { shownMessageKey } from "./dispatchModel";
import type { DispatchTableRow } from "./dispatchTableRow";
import { unreadMessageShownBy } from "./readHighlighted";

/**
 * Reads the message the highlighted row shows. The row's Message cell carries the driver's message, and a dispatcher
 * who has moved the highlight onto it has read it there: a `Read Feed Item` receipt goes out for that message, at
 * either grain, unless the member had read it already. A driver row whose cell shows a required TMS event reads
 * nothing, since the message behind it was never shown. The receipt is the person's own, so a colleague's unread state
 * is untouched except under team dispatching, exactly as any read receipt behaves. Pass null while rows are ticked:
 * only a single selection reads, so a multi-selection, however many rows, reads nothing.
 */
export function useReadHighlighted(highlighted: DispatchTableRow | null): void {
  const { accountEvent } = useContext(ActionContext);
  // The row and what it shows: a new message from the same driver re-fires, a re-render of the same row does not.
  const shownKey = highlighted ? `${highlighted.id}:${shownMessageKey(highlighted)}` : null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: `shownKey` is the highlighted row's identity for this effect
  useEffect(() => {
    if (!highlighted || !accountEvent) return;
    const shown = unreadMessageShownBy(highlighted);
    if (shown) accountEvent("Read Feed Item", { feedId: shown.feedId, feedItemId: shown.itemId });
  }, [shownKey, accountEvent]);
}
